import { ReactNode, useEffect, useState } from "react";
import { MatomoContext, createInstance } from "@jonkoops/matomo-tracker-react";
import { useAppSelector } from "../store/hooks";
import {
  selectIsTenantResolved,
  selectIsTrackingEnabled,
} from "../store/tenant-config/tenant-config.slice";
import { environment } from "../../../environments/environment";

type MatomoInstance = ReturnType<typeof createInstance>;

export interface MatomoProviderGateProps {
  children: ReactNode;
}

/**
 * Creates the Matomo tracker lazily, only once the tenant config has been fully
 * resolved, so the per-tenant tracking decision is final before the tracker is
 * built.
 *
 * For tracking-disabled tenants (e.g. Bethmann) the instance is created with
 * `disabled: true`. The underlying tracker then never injects the matomo.js
 * script and never sends any request, giving truly zero network hits. Until the
 * tenant is resolved the context value is `null`, which makes every useMatomo()
 * call a no-op, so nothing is tracked before the decision is made.
 */
export function MatomoProviderGate({ children }: MatomoProviderGateProps) {
  const isTenantResolved = useAppSelector(selectIsTenantResolved);
  const isTrackingEnabled = useAppSelector(selectIsTrackingEnabled);
  const [instance, setInstance] = useState<MatomoInstance | null>(null);

  useEffect(() => {
    if (instance === null && isTenantResolved) {
      setInstance(
        createInstance({
          urlBase: environment.matomoUrl,
          siteId: environment.matomoSiteId,
          linkTracking: false,
          disabled: !isTrackingEnabled,
        })
      );
    }
  }, [instance, isTenantResolved, isTrackingEnabled]);

  return (
    <MatomoContext.Provider value={instance}>
      {children}
    </MatomoContext.Provider>
  );
}
